import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Warehouse, History, ArrowLeft, AlertTriangle, FileSpreadsheet, Clock } from "lucide-react";
import { inr } from "@/components/billing/BillingParts";
import { reorderWhatsApp } from "@/components/purchases/PurchaseParts";
import Items from "@/components/inventory/Items";
import ExcelPanel from "@/components/ExcelPanel";
import { Godowns, StockMoves } from "@/components/inventory/GodownsMoves";
import { ExpiryPanel } from "@/components/inventory/Expiry";
import { BusinessSwitcher } from "@/components/biz/BusinessSwitcher";
import AdSlider from "@/components/ads/AdSlider";

const TABS = [["items", "Items & stock", Package], ["expiry", "Batches & expiry", Clock], ["godowns", "Godowns", Warehouse], ["moves", "Stock history", History], ["excel", "Import / Export", FileSpreadsheet]];

export function InventoryPanel({ clientId }) {
  const qs = clientId ? `?client_id=${clientId}` : "";
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "items";
  const [summary, setSummary] = useState(null);
  const [godowns, setGodowns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [business, setBusiness] = useState("");
  const load = () => { api.get(`/inventory/summary${qs}`).then(r => setSummary(r.data)); api.get(`/inventory/godowns${qs}`).then(r => setGodowns(r.data)); api.get(`/purchases/suppliers${qs}`).then(r => setSuppliers(r.data)).catch(() => {}); };
  useEffect(() => { load(); (clientId ? api.get(`/billing/staff/${clientId}/invoices`).then(r => r.data.profile?.business_name) : api.get("/billing/profile").then(r => r.data.business_name)).then(n => setBusiness(n || "")).catch(() => {}); }, [clientId]); // eslint-disable-line
  const setTab = (k) => setParams(p => { const n = new URLSearchParams(p); n.set("tab", k); return n; });

  return (
    <div className="space-y-6" data-testid="inventory-panel">
      {summary ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="inv-kpis">
          {[["Items", `${summary.products} products · ${summary.services} services`, "kpi-items"], ["Stock value (cost)", inr(summary.stock_value), "kpi-stock-value"], ["Stock value (sale)", inr(summary.sale_value), "kpi-sale-value"], ["Low stock", `${summary.low_stock.length} item(s)`, "kpi-low"], ["Expiring ≤30d", `${summary.expiry?.count || 0} batch(es)`, "kpi-expiry"]].map(([l, v, t]) => (
            <div key={t} data-testid={t} onClick={t === "kpi-expiry" ? () => setTab("expiry") : undefined} className={`bg-white border p-4 ${(t === "kpi-low" && summary.low_stock.length) || (t === "kpi-expiry" && summary.expiry?.count) ? "border-amber-400" : "border-zinc-200"} ${t === "kpi-expiry" ? "cursor-pointer" : ""}`}>
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">{l}</div>
              <div className="font-heading font-bold text-lg tabular mt-1">{v}</div>
            </div>
          ))}
        </div>
      ) : null}
      {summary?.low_stock.length ? (
        <div className="border border-amber-300 bg-amber-50 p-3 text-xs flex flex-wrap gap-x-4 gap-y-1 items-center" data-testid="low-stock-alert">
          <span className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={13} /> Low stock:</span>
          {summary.low_stock.map(i => <button key={i.id} data-testid={`low-reorder-${i.id}`} onClick={() => reorderWhatsApp(i, suppliers.find(s => s.id === i.supplier_id), business)} title="Send WhatsApp purchase order" className="tabular px-2 py-0.5 border border-amber-300 bg-white hover:bg-emerald-600 hover:text-white hover:border-emerald-600">{i.name} <b>{i.total_stock}</b>/{i.low_stock_level} {i.unit} ↗</button>)}
        </div>
      ) : null}
      <nav className="flex gap-1 border-b border-zinc-200 overflow-x-auto" data-testid="inv-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`inv-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold px-3 py-2.5 border-b-2 transition-colors ${tab === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}><Icon size={14} /> {l}</button>
        ))}
      </nav>
      {tab === "items" ? <Items qs={qs} godowns={godowns} categories={summary?.categories || []} suppliers={suppliers} business={business} onChanged={load} /> : null}
      {tab === "expiry" ? <ExpiryPanel qs={qs} /> : null}
      {tab === "godowns" ? <Godowns qs={qs} godowns={godowns} onChanged={load} /> : null}
      {tab === "moves" ? <StockMoves qs={qs} /> : null}
      {tab === "excel" ? <ExcelPanel qs={qs} onChanged={load} /> : null}
    </div>
  );
}

export default function MyInventory() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-zinc-500 tracking-[0.2em] uppercase text-xs">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="my-inventory">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/my-portal" data-testid="inventory-back" className="p-2 border border-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors"><ArrowLeft size={14} /></Link>
            <div className="min-w-0"><div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">My Inventory</div><div className="font-heading font-bold tracking-tight truncate">{user.name}</div></div>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar [&>*]:shrink-0">
            <BusinessSwitcher />
            <Link to="/my-billing" data-testid="inventory-to-billing" className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors">My Billing</Link>
            <button data-testid="inventory-signout" onClick={logout} className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors">Sign out</button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 -mb-2"><AdSlider placement="business" slim /></div>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6"><InventoryPanel clientId={null} /></main>
    </div>
  );
}

export function ClientInventory() {
  const { id } = useParams();
  const [name, setName] = useState("");
  useEffect(() => { api.get(`/billing/staff/${id}/invoices`).then(r => setName(r.data.profile?.business_name || "")).catch(() => {}); }, [id]);
  return (
    <div className="space-y-6" data-testid="client-inventory-page">
      <header>
        <Link to="/clients" data-testid="inventory-back" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900 flex items-center gap-1"><ArrowLeft size={12} /> Clients</Link>
        <h1 className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2">{name || "Client"} · Inventory</h1>
        <p className="text-sm text-zinc-600 mt-1">Items, stock levels and godowns maintained for this client. Edits here reflect in the client's portal.</p>
      </header>
      <InventoryPanel clientId={id} />
    </div>
  );
}
